// src/lib/peerTakes.js
// STATUS: active | UPDATED: 2026-09-10
// LOG:
//   2026-09-10  New. A colleague showing how they actually say it.
// Peer teaching.
//
// A take is one person's version of a step, recorded out loud. It is NOT the
// method and nothing here may ever be fed to the coach as approved knowledge.
// `method_contributions` is Jazz. This is the floor.

import { supabase } from './supabase'

export async function createTake({ dealershipId, userId, stepNumber, body }) {
  if (!dealershipId || !userId || !stepNumber) return { error: 'missing required fields' }
  const { data, error } = await supabase
    .from('peer_takes')
    .insert({
      dealership_id: dealershipId,
      author_id: userId,
      step_number: stepNumber,
      body: String(body || '').trim().slice(0, 4000),
      status: 'draft',
    })
    .select('id, body, status, review_note, step_number, created_at')
    .single()
  return { data, error }
}

// The private look before the floor sees it. Never a gate: the caller can
// publish whatever comes back, including nothing.
export async function reviewTake(takeId) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) return { note: null }
  try {
    const res = await fetch('/.netlify/functions/review-take', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ takeId }),
    })
    if (!res.ok) return { note: null }
    return await res.json()
  } catch {
    return { note: null }
  }
}

export async function setTakeStatus(takeId, status) {
  const { error } = await supabase.from('peer_takes').update({ status }).eq('id', takeId)
  return { error }
}

export async function updateTakeBody(takeId, body) {
  const { error } = await supabase
    .from('peer_takes')
    .update({ body: String(body || '').trim().slice(0, 4000) })
    .eq('id', takeId)
  return { error }
}

// Mine for this step, any status. RLS keeps drafts to their author, which is
// what makes recording one a low risk thing to try.
export async function myTakes(userId, stepNumber) {
  const { data } = await supabase
    .from('peer_takes')
    .select('id, body, status, review_note, reviewed_at, step_number, created_at')
    .eq('author_id', userId)
    .eq('step_number', stepNumber)
    .order('created_at', { ascending: false })
  return { data: data || [] }
}

// What the floor has published for this step, mine excluded because I am not
// a teammate to myself.
export async function floorTakes(dealershipId, stepNumber, excludeUserId) {
  if (!dealershipId) return { data: [] }
  let q = supabase
    .from('peer_takes')
    .select('id, body, author_id, step_number, created_at')
    .eq('dealership_id', dealershipId)
    .eq('step_number', stepNumber)
    .eq('status', 'published')
    .order('created_at', { ascending: false })
    .limit(10)
  if (excludeUserId) q = q.neq('author_id', excludeUserId)
  const { data } = await q
  return { data: data || [] }
}
